import { useRef, useState } from "react";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { UploadCloud, FileUp, Loader2, CheckCircle2 } from "lucide-react";

const EMPTY = { land: "", blatt: "", jahr: "", nummer: "", page_start: "", page_end: "", datum: "" };

// land_blatt_jahr_nummer_start_end.pdf  (gleiches Schema wie die Crawler)
const parseFilename = (name) => {
  const m = /^([a-zA-Z]{2})_([a-zA-Z0-9]+)_(\d{4})_(\d+)_(\d+)_(\d+)\.pdf$/.exec(name || "");
  if (!m) return null;
  return {
    land: m[1].toLowerCase(), blatt: m[2].toLowerCase(), jahr: m[3],
    nummer: String(parseInt(m[4], 10)), page_start: String(parseInt(m[5], 10)),
    page_end: String(parseInt(m[6], 10)),
  };
};

const FIELDS = [
  { k: "land", label: "Bundesland", ph: "z.B. st", w: "col-span-1" },
  { k: "blatt", label: "Heft / Blatt", ph: "z.B. mbl", w: "col-span-1" },
  { k: "jahr", label: "Jahr", ph: "2026", w: "col-span-1" },
  { k: "nummer", label: "Heftnummer", ph: "20", w: "col-span-1" },
  { k: "page_start", label: "von-Seite", ph: "219", w: "col-span-1" },
  { k: "page_end", label: "bis-Seite", ph: "235", w: "col-span-1" },
];

export default function UploadPdfDialog({ onUploaded }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const reset = () => { setFile(null); setForm(EMPTY); };
  const setOpenState = (v) => { setOpen(v); if (!v) reset(); };

  const onFile = (f) => {
    if (!f) return;
    setFile(f);
    const parsed = parseFilename(f.name);
    const datum = dayjs().format("YYYY-MM-DD");
    if (parsed) {
      setForm({ ...parsed, datum });
    } else {
      setForm({ ...EMPTY, datum });
      toast.warning("Dateiname entspricht nicht dem Schema – bitte Felder manuell ausfüllen.");
    }
  };

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!file) { toast.error("Bitte zuerst eine PDF-Datei wählen"); return; }
    const missing = FIELDS.filter((f) => !String(form[f.k] ?? "").trim()).map((f) => f.label);
    if (missing.length) { toast.error("Bitte alle Felder ausfüllen", { description: missing.join(", ") }); return; }
    const fd = new FormData();
    fd.append("file", file);
    FIELDS.forEach((f) => fd.append(f.k, String(form[f.k]).trim()));
    fd.append("datum", form.datum || "");
    setUploading(true);
    try {
      const r = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PDF hochgeladen – Verarbeitung läuft", { description: r.data.document.filename });
      setOpenState(false);
      onUploaded && onUploaded(r.data.document);
    } catch (e) {
      toast.error("Upload fehlgeschlagen", { description: e?.response?.data?.detail || "" });
    } finally { setUploading(false); }
  };

  return (
    <>
      <Button size="sm" className="bg-[#0F4C81] hover:bg-[#0d3f6b] text-white" onClick={() => setOpen(true)} data-testid="pp-upload-open" title="PDF manuell hochladen">
        <UploadCloud className="h-4 w-4 mr-1.5" /> PDF hochladen
      </Button>

      <Dialog open={open} onOpenChange={setOpenState}>
        <DialogContent className="sm:max-w-lg" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">PDF manuell hochladen</DialogTitle>
          </DialogHeader>

          {/* Datei wählen */}
          <input ref={inputRef} type="file" accept="application/pdf" className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])} data-testid="upload-file-input" />
          <button type="button" onClick={() => inputRef.current?.click()} data-testid="upload-pick" title="PDF-Datei auswählen"
            className={`w-full rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors ${file ? "border-green-400 bg-green-50" : "border-gray-300 hover:border-[#0F4C81] hover:bg-[#0F4C81]/5"}`}>
            {file ? (
              <span className="flex items-center justify-center gap-2 text-sm font-medium text-green-700">
                <CheckCircle2 className="h-4 w-4" /> {file.name}
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <FileUp className="h-4 w-4" /> PDF auswählen …
              </span>
            )}
          </button>

          {file && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">Aus dem Dateinamen ermittelt – bei Bedarf anpassen:</p>
              <div className="grid grid-cols-3 gap-3">
                {FIELDS.map((f) => (
                  <div key={f.k} className={f.w}>
                    <Label className="text-xs">{f.label}</Label>
                    <Input value={form[f.k]} placeholder={f.ph}
                      onChange={(e) => upd(f.k, e.target.value)}
                      data-testid={`upload-field-${f.k}`} />
                  </div>
                ))}
                <div className="col-span-3">
                  <Label className="text-xs">Datum <span className="text-gray-400">(nicht im Dateinamen – Vorgabe: heute)</span></Label>
                  <Input type="date" value={form.datum}
                    onChange={(e) => upd("datum", e.target.value)}
                    data-testid="upload-field-datum" />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenState(false)} data-testid="upload-cancel" title="Abbrechen">Abbrechen</Button>
            <Button className="bg-[#0F4C81] hover:bg-[#0d3f6b] text-white" onClick={submit} disabled={!file || uploading} data-testid="upload-submit" title="PDF hochladen">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <UploadCloud className="h-4 w-4 mr-1.5" />} Hochladen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
